import {
  Box,
  Form,
  Card,
  Divider,
  FormLayout,
  InlineGrid,
  Select,
  Text,
  TextField,
  BlockStack,
  useBreakpoints,
  Checkbox,
  ContextualSaveBar,
} from '@shopify/polaris';

import { useEffect, useState } from 'react';
import helpers from 'helpers';
import { lengthLessThan, lengthMoreThan, notEmptyString, useField, useForm } from '@shopify/react-form';

import { TypedUser } from 'interface/user.model';
import DateTimeInput from 'components/dateTimeInput';
import __ from 'languages/index';
import { useUpdateUser } from 'queries/users.query';
import { useNotification } from 'context/NotificationContext';
import { useUserRoleContext } from 'context/UserRoleContext';

/**
 *   Create upload Modal for Notification
 */
export default function UserEditInformation({ onClose, entity }: { onClose: Function; entity?: TypedUser }) {
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateUser();

  const { smUp } = useBreakpoints();

  /** Lấy user role ra ... */
  const { allRoleSelection: allRoles } = useUserRoleContext();

  /** Hết code lấy user role ra ... */

  const useFields = {
    user_login: useField<string>({
      value: entity?.user_login || '',
      validates: [
        lengthLessThan(60, 'Không được dài hơn 60 ký tự.'),
        lengthMoreThan(2, 'Không được ngắn hơn 2 ký tự.'),
        (inputValue) => {
          if (inputValue.length > 0) {
            if (helpers.isUTF8(inputValue)) {
              return 'Không được dùng mã Unicode trong trường này!';
            }
            if (/^[a-zA-Z0-9_.]+$/.exec(inputValue) === null) {
              return 'Chỉ sử dụng chữ cái, số và dấu chấm';
            }
          }
        },
      ],
    }),

    user_email: useField<string>({
      value: entity?.user_email,
      validates: [
        (inputValue) => {
          if (inputValue && !helpers.isEmail(inputValue)) {
            return 'Your email is not valid!';
          }
        },
      ],
    }),

    display_name: useField<string>({
      value: entity?.display_name || '',
      validates: [lengthLessThan(60, 'Tên của bạn quá dài!'), lengthMoreThan(2, 'Tên của bạn quá ngắn!')],
    }),

    bio: useField<string>({
      value: entity?.bio || '',
      validates: [lengthLessThan(250, 'Mô tả quá dài!')],
    }),

    user_address: useField<string>({
      value: entity?.user_address || '',
      validates: [lengthLessThan(250, 'Địa chỉ quá dài!')],
    }),

    user_birthday: useField<string>({
      value: entity?.user_birthday,
      validates: [],
    }),

    user_phonenumber: useField<string>({
      value: entity?.user_phonenumber,
      validates: [
        (inputValue) => {
          if (inputValue) {
            if (!new RegExp('^[0-9]+$', 'g').test(inputValue)) {
              return 'Định dạng số điện thoại không hợp lệ. Chỉ dùng số và dấu cách!';
            }
          }
        },
      ],
    }),

    user_status: useField<number>({
      value: entity?.user_status || 0,
      validates: [],
    }),

    user_role: useField<string>({
      value: entity?.user_role,
      validates: [],
    }),

    user_title: useField<string>({
      value: entity?.user_title,
      validates: [],
    }),

    user_address_city: useField<string>({
      value: entity?.user_address_city ?? '',
      validates: [],
    }),

    user_address_ward: useField<string>({
      value: entity?.user_address_ward ?? '',
      validates: [],
    }),
  };

  const {
    fields,
    submit,
    submitting,
    dirty,
    reset: resetForm,
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateEntity({
          user_id: entity?.user_id,
          user_status: values.user_status,
          user_role: values.user_role || null,
          user_login: values.user_login || undefined,
          display_name: values.display_name,
          bio: values.bio,
          user_title: values.user_title,
          user_address: values.user_address,
          user_address_city: values.user_address_city,
          user_address_ward: values.user_address_ward,
          user_phonenumber: values.user_phonenumber,
          user_email: values.user_email,
          user_birthday: values.user_birthday || null,
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  const { addNotification } = useNotification();

  useEffect(() => {
    if (updateSuccess) {
      addNotification('info', __('update_successfully'));
      // resetForm();
      // onClose();
    }
  }, [updateSuccess]);

  return (
    <Form onSubmit={submit}>
      <BlockStack gap={{ xs: '800', sm: '400' }}>
        <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
          <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">
                Tùy chọn
              </Text>
              <Text as="p" variant="bodyMd">
                Các tùy chọn
              </Text>
            </BlockStack>
          </Box>
          <Card roundedAbove="sm">
            <BlockStack gap="400">
              <FormLayout>
                <Checkbox
                  label="Cho phép người dùng đăng nhập"
                  helpText="Nếu tắt, người dùng này không thể đăng nhập nữa."
                  checked={useFields.user_status.value === 1}
                  onChange={(e) => {
                    useFields.user_status.onChange(Number(e));
                  }}
                />

                <Select
                  label="Vai trò của người dùng"
                  options={[
                    ...[
                      {
                        label: 'Không thay đổi vai trò cũ',
                        value: '',
                      },
                    ],
                    ...allRoles,
                  ]}
                  onChange={(selected) => {
                    useFields.user_role.onChange(selected);
                  }}
                  value={useFields.user_role.value}
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </InlineGrid>

        {smUp ? <Divider /> : null}

        <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
          <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">
                Thông tin chung
              </Text>
              <Text as="p" variant="bodyMd">
                Càng chi tiết càng tốt.
              </Text>
            </BlockStack>
          </Box>
          <Card roundedAbove="sm">
            <BlockStack gap="400">
              <FormLayout>
                <TextField
                  autoFocus
                  autoComplete="off"
                  label="User name"
                  helpText={<p>User name không được phép trùng lặp với bất kỳ một tài khoản nào!</p>}
                  {...fields.user_login}
                />

                <TextField
                  label="Tên hiển thị"
                  autoComplete="off"
                  {...fields.display_name}
                  connectedLeft={
                    <Select
                      value={useFields.user_title.value || undefined}
                      options={[
                        { value: '', label: 'Không set' },
                        { value: 'mr', label: 'Anh' },
                        { value: 'miss', label: 'Cô' },
                        { value: 'mrs', label: 'Chị' },

                        { value: 'madam', label: 'Quý bà' },
                        { value: 'sir', label: 'Quý ông' },
                      ]}
                      onChange={(e) => useFields.user_title.onChange(e)}
                      label={''}
                    />
                  }
                />

                <DateTimeInput
                  onDateChange={(dateObjectOrnull) => {
                    if (!dateObjectOrnull) {
                      fields.user_birthday.onChange(``);
                    } else {
                      let { day, month, year } = dateObjectOrnull;
                      fields.user_birthday.onChange(`${year}-${month}-${day}`);
                    }
                  }}
                  label={'Sinh nhật'}
                  initialDate={fields.user_birthday.value}
                  error={fields.user_birthday.error}
                />

                <TextField
                  maxLength={250}
                  max={250}
                  showCharacterCount={true}
                  autoComplete="off"
                  label="Giới thiệu ngắn"
                  {...fields.bio}
                  multiline={3}
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </InlineGrid>

        {smUp ? <Divider /> : null}
        <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
          <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">
                Thông tin liên hệ
              </Text>
              <Text as="p" variant="bodyMd">
                Càng nhiều thông tin càng tốt
              </Text>
            </BlockStack>
          </Box>
          <Card roundedAbove="sm">
            <BlockStack gap="400">
              <FormLayout>
                <FormLayout.Group>
                  <TextField label="Số điện thoại" autoComplete="off" type="tel" inputMode="numeric" pattern="[0-9]*" {...fields.user_phonenumber} />
                  <TextField label="Email" autoComplete="off" {...fields.user_email} />
                </FormLayout.Group>

                <TextField label="Địa chỉ" autoComplete="off" {...fields.user_address} />
              </FormLayout>
            </BlockStack>
          </Card>
        </InlineGrid>
      </BlockStack>

      <br />
      <br />

      {dirty && (
        <ContextualSaveBar
          message="Thay đổi chưa được lưu"
          saveAction={{
            content: __('update_user_button_label'),
            onAction: () => submit(),
            loading: updating,
          }}
          discardAction={{
            content: 'Thu hồi',
            onAction: () => resetForm(),
          }}
        />
      )}
      <br />
      <br />
      <br />
      <br />
      <br />
    </Form>
  );
}

